import { useState } from "react";
import { Link } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { T } from "../theme";
import { useTheme } from "../ThemeProvider";

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── SVG icons ── */
const SunIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const MenuIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ── STYLED ── */
const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 200;
  height: 62px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 clamp(16px, 4vw, 48px);
  background: color-mix(in srgb, var(--bg) 88%, transparent);
  border-bottom: 1px solid ${T.border};
  backdrop-filter: blur(16px) saturate(1.4);
  gap: 12px;
`;

const LogoLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  img {
    height: 26px;
    width: auto;
  }

  span {
    font-size: 0.78rem;
    font-weight: 600;
    color: ${T.muted};
    padding-left: 10px;
    border-left: 1px solid ${T.border};
    letter-spacing: 0.01em;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
`;

const NavA = styled.a`
  font-size: 0.82rem;
  font-weight: 500;
  color: ${T.muted};
  padding: 6px 10px;
  border-radius: 7px;
  white-space: nowrap;
  transition: color .12s, background .12s;
  &:hover {
    color: ${T.text};
    background: color-mix(in srgb, var(--text) 5%, transparent);
  }

  @media (max-width: 860px) { display: none; }
`;

/* Link de destaque (acompanhar candidatura). */
const TrackLink = styled.a`
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--purple);
  padding: 7px 13px;
  border-radius: 8px;
  border: 1.5px solid color-mix(in srgb, var(--purple) 36%, transparent);
  white-space: nowrap;
  transition: background .12s, border-color .12s;
  &:hover { background: var(--purple-soft); border-color: var(--purple); }

  @media (max-width: 600px) { display: none; }
`;

const IconBtn = styled.button`
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid ${T.border};
  border-radius: 8px;
  color: ${T.muted};
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color .12s, color .12s, background .12s;

  &:hover {
    border-color: ${T.borderMd};
    color: ${T.text};
    background: color-mix(in srgb, var(--text) 4%, transparent);
  }
`;

const HamBtn = styled(IconBtn)`
  display: none;
  border: none;
  @media (max-width: 600px) { display: flex; }
`;

/* ── Mobile drawer ── */
const Drawer = styled.div`
  @media (max-width: 600px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 62px;
    left: 0;
    right: 0;
    background: ${T.card};
    border-bottom: 1px solid ${T.border};
    box-shadow: ${T.shadow};
    padding: 8px 0 12px;
    z-index: 199;
    animation: ${slideDown} 0.18s ease both;

    ${(p) => !p.$open && css`display: none;`}
  }

  display: none;
`;

const DrawerLink = styled.a`
  display: flex;
  align-items: center;
  padding: 13px clamp(16px, 4vw, 48px);
  font-size: 0.92rem;
  font-weight: 600;
  color: ${T.muted};
  transition: background .12s, color .12s;
  &:hover {
    background: color-mix(in srgb, var(--text) 4%, transparent);
    color: ${T.text};
  }
`;

const DrawerDivider = styled.div`
  height: 1px;
  background: ${T.border};
  margin: 6px clamp(16px, 4vw, 48px);
`;

const DrawerTheme = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px clamp(16px, 4vw, 48px);
  font-size: 0.92rem;
  font-weight: 600;
  color: ${T.muted};
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  width: 100%;
  transition: background .12s, color .12s;
  &:hover {
    background: color-mix(in srgb, var(--text) 4%, transparent);
    color: ${T.text};
  }
`;

export default function Header() {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const logoStyle = theme === "dark" ? { filter: "brightness(0) invert(1)" } : {};

  return (
    <>
      <a href="#main-content" className="skip-link">Pular para o conteúdo principal</a>
      <Bar>
        <LogoLink as={Link} to="/" onClick={close}>
          <img src="/guebly-logo.png" alt="Guebly" style={logoStyle} />
          <span>Carreiras</span>
        </LogoLink>

        <Nav>
          <NavA as={Link} to="/#empresas">Empresas</NavA>
          <NavA as={Link} to="/#vagas">Vagas</NavA>
          <NavA as={Link} to="/#beneficios">Cultura</NavA>
          <NavA as={Link} to="/#processo">Processo</NavA>
          <NavA as={Link} to="/#faq">FAQ</NavA>
          <TrackLink as={Link} to="/acompanhar">Minha candidatura</TrackLink>
          <IconBtn
            onClick={toggle}
            aria-label={theme === "dark" ? "Modo claro" : "Modo escuro"}
            title={theme === "dark" ? "Modo claro" : "Modo escuro"}
          >
            {theme === "dark" ? <SunIcon /> : <MoonIcon />}
          </IconBtn>
          <HamBtn
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </HamBtn>
        </Nav>
      </Bar>

      {open && (
        <Drawer id="mobile-menu" $open={open}>
          <DrawerLink as={Link} to="/#empresas" onClick={close}>Nossas empresas</DrawerLink>
          <DrawerLink as={Link} to="/#vagas" onClick={close}>Vagas abertas</DrawerLink>
          <DrawerLink as={Link} to="/#beneficios" onClick={close}>Cultura & benefícios</DrawerLink>
          <DrawerLink as={Link} to="/#processo" onClick={close}>Processo seletivo</DrawerLink>
          <DrawerLink as={Link} to="/#faq" onClick={close}>Perguntas frequentes</DrawerLink>
          <DrawerLink as={Link} to="/acompanhar" onClick={close}>Minha candidatura</DrawerLink>
          <DrawerDivider />
          <DrawerTheme onClick={() => { toggle(); close(); }}>
            {theme === "dark"
              ? <><SunIcon /> Modo claro</>
              : <><MoonIcon /> Modo escuro</>
            }
          </DrawerTheme>
        </Drawer>
      )}
    </>
  );
}
